import { useCallback } from 'react';
import {
  Channel,
  EventType,
  RestService,
  useAppBridge,
  useChannelContext,
  useEventBusEmit,
  useSessionService,
  useUserData,
} from 'scbk-services';
import { usePageMove } from 'scbk-shared-module/hooks';

type LogoutCallback = () => void | Promise<void>;
/**
 * 로그아웃 옵션
 */
type LogoutOptions = {
  onLogout?: LogoutCallback;
};

/**
 * 로그아웃 처리 훅 함수
 */
const useLogout = () => {
  const { channel } = useChannelContext();
  const { clearUserData } = useUserData();
  const { doNLogout } = useAppBridge();
  const { executeLogout } = useSessionService();
  const { goLogin } = usePageMove(); // 페이지 이동
  const emit = useEventBusEmit();

  /**
   * 로그아웃 처리를 수행한다.
   * @param options 로그아웃 Options
   *
   * - onLogout : 로그아웃 callback
   */
  const doLogout = useCallback(
    async (options?: LogoutOptions) => {
      const onLogout = options?.onLogout ?? goLogin;

      const runLogoutCallback = async (callback?: () => void | Promise<void>) => {
        if (!callback) {
          return;
        }

        return Promise.resolve(callback());
      };

      try {
        //*********************************************
        //로그아웃 수행 API 호출
        //*********************************************
        await executeLogout();
      } finally {
        //유저 정보 초기화
        clearUserData();

        if (channel === Channel.MB) {
          //*********************************************
          //콜백함수가 지정이 되어 있다면 콜백함수 실행
          //지정된 콜백함수가 없다면 goLogin 함수 실행
          //*********************************************
          await runLogoutCallback(onLogout);

          // 네이티브 로그아웃 이벤트 전송
          await doNLogout('N');
        } else {
          //*********************************************
          //인뱅 세션 타이머 종료
          //[2026.04.17 최성돈] 세션타이머 종료 이벤트로 처리
          //*********************************************
          emit(EventType.ClearSessionTimer);

          //*********************************************
          //콜백함수가 지정이 되어 있다면 콜백함수 실행
          //지정된 콜백함수가 없다면 goLogin 함수 실행
          //*********************************************
          await runLogoutCallback(onLogout);
        }

        // 기호출 API abort
        RestService.abortAll();
      }
    },
    [channel, clearUserData, doNLogout, goLogin],
  );

  return {
    doLogout,
  };
};

export { useLogout };
